import * as React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Modal, Select, Alert } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import {
	getDisabledDate,
	getDisabledDateTime,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import FieldTechSelect from '../field_tech_select/FieldTechSelect';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { isDifferentAssignedSupplier } from '../../utils/WorkOrderUtils';
import moment from 'moment-timezone';
import { DateTimeWithTimeZone } from '../date_time_with_time_zone/DateTimeWithTimeZone';
import { wallTimeToUtcIso } from '../../utils/DataFormatterUtils';

const FormItem = Form.Item;

interface ScheduleServiceCallFormProps extends FormComponentProps {
	onSubmit: any;
	onCancel: any;
	visible: boolean;
	createErrors: any;
	updateErrors: any;

	isReschedule?: boolean;
	fetchMultipleStockLocations: any;
	fetchStockLocations: any;
	stockLocations: any;
	creating: boolean;
	updating: boolean;
	formData: any;
	currentUser: any;
	initialTimeZone: any;
}

class ScheduleServiceCallModalForm extends React.Component<ScheduleServiceCallFormProps, any> {
	_resolveDispatcherKeys = (formData: any) => ({
		locationId:
			nullSafeGet('workOrder.locationId', formData) ||
			nullSafeGet('workOrder.location.id', formData),
		supplierFacilityId: nullSafeGet('workOrder.contractorWo', formData)
			? nullSafeGet('workOrder.contractorWo.subSupplierFacilityId', formData)
			: nullSafeGet('workOrder.supplierFacilityId', formData) || formData.supplierFacilityId,
	});

	componentDidMount(): void {
		const { form, fetchStockLocations, formData } = this.props;

		const { locationId } = this._resolveDispatcherKeys(formData);

		locationId &&
			fetchStockLocations({
				locationId,
			}).then((stockLocations) => {
				stockLocations &&
					stockLocations.length > 0 &&
					form.setFieldsValue &&
					form.setFieldsValue({ stockLocationIds: stockLocations.map((_) => _.id) });
			});
	}

	render() {
		const {
			visible,
			isReschedule,
			fetchMultipleStockLocations,
			fetchStockLocations,
			stockLocations,
			onCancel,
			onSubmit,
			form,
			formData,
			createErrors,
			updateErrors,
			currentUser,
			creating,
			updating,
			initialTimeZone,
		} = this.props;
		const { getFieldDecorator, getFieldValue } = form;
		const okText = isReschedule ? 'Reschedule' : 'Schedule';
		const titleText = isReschedule ? 'Reschedule Service' : 'Schedule Service';
		getFieldDecorator(`id`, { initialValue: formData.id });
		getFieldDecorator(`workOrderId`, { initialValue: nullSafeGet('workOrder.id', formData) });
		getFieldDecorator(`supplierFacilityId`, {
			initialValue:
				formData.supplierFacilityId || nullSafeGet('workOrder.supplierFacilityId', formData),
		});

		const differentAssignedSupplier = isDifferentAssignedSupplier(formData, currentUser);

		const leadTechnicianEmail = getFieldValue('leadTechnicianEmail');
		const additionalTechnicianEmails = getFieldValue('additionalTechnicianEmails');

		const scheduleTimeRequired =
			!leadTechnicianEmail && !nullSafeGet('0', additionalTechnicianEmails);

		const location = nullSafeGet('workOrder.location', formData);
		const serviceScheduledAtTimeZone =
			form.getFieldValue('serviceScheduledAtTimeZone') || nullSafeGet('timeZoneId', location);

		const { locationId, supplierFacilityId } = this._resolveDispatcherKeys(formData);

		return (
			<Modal
				visible={visible}
				width={600}
				title={titleText}
				okText={okText}
				onCancel={onCancel}
				onOk={onSubmit}
				closable={false}
				confirmLoading={creating || updating}
			>
				<Form layout="vertical" className="supplierForm">
					{createErrors.length > 0 ? (
						<FormItem>
							<Alert message={createErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{updateErrors.length > 0 ? (
						<FormItem>
							<Alert message={updateErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{differentAssignedSupplier ? (
						getFieldDecorator('leadTechnicianEmail', {
							initialValue: formData.leadTechnicianEmail,
						})
					) : (
						<Form.Item label="Lead Technician">
							{getFieldDecorator('leadTechnicianEmail', {
								initialValue:
									formData.leadTechnicianEmail ||
									(nullSafeGetOrElse('workOrder.supplierFacilityId', formData, -1) ===
									nullSafeGetOrElse('workOrder.assignedPrimaryTechContact.facilityId', formData, -2)
										? nullSafeGet('workOrder.assignedPrimaryTechContact.email', formData)
										: undefined),
							})(
								<FieldTechSelect
									locationId={locationId}
									supplierFacilityId={supplierFacilityId}
									isReschedule={isReschedule}
									allowClear={true}
									targetCollectionName="scheduleServiceCallDropdown"
								/>
							)}
						</Form.Item>
					)}
					{differentAssignedSupplier ? (
						getFieldDecorator('additionalTechnicianEmails', {
							initialValue: formData.additionalTechnicianEmails,
						})
					) : (
						<Form.Item label="Additional Technicians">
							{getFieldDecorator('additionalTechnicianEmails', {
								initialValue: formData.additionalTechnicianEmails,
							})(
								<FieldTechSelect
									mode="multiple"
									locationId={locationId}
									supplierFacilityId={supplierFacilityId}
									isReschedule={isReschedule}
									targetCollectionName="scheduleServiceCallAdditionalTechsDropdown"
								/>
							)}
						</Form.Item>
					)}
					<DateTimeWithTimeZone
						form={form}
						location={location}
						displayName={'Scheduled Arrival Time'}
						timeZoneName={'serviceScheduledAtTimeZone'}
						dateName={'serviceScheduledAt'}
						initialValue={formData.serviceScheduledAt}
						initialTimeZone={initialTimeZone}
						minuteStep={15}
						rules={[
							{
								type: 'object',
								required: scheduleTimeRequired,
								message: 'Please select an estimated arrival time.',
							},
							{
								message: 'Service scheduled time must be after the current time.',
								validator: (_, value) => {
									if (!value) return Promise.resolve();

									const serviceScheduledUtcIso = wallTimeToUtcIso(
										value,
										serviceScheduledAtTimeZone
									);
									const serviceScheduledUtc = moment(serviceScheduledUtcIso).utc();

									const nowUtc = moment().utc().subtract(1, 'minute');

									if (serviceScheduledUtc.isBefore(nowUtc)) {
										return Promise.reject(
											new Error('Service scheduled time must be after the current time.')
										);
									}
									return Promise.resolve();
								},
							},
						]}
						disabledDate={getDisabledDate(moment(), serviceScheduledAtTimeZone)}
						disabledTime={getDisabledDateTime(moment(), serviceScheduledAtTimeZone)}
					/>
					{differentAssignedSupplier ? (
						getFieldDecorator('stockLocationIds', {
							initialValue: formData.stockLocationIds,
						})
					) : (
						<Form.Item label="Stock Locations">
							{getFieldDecorator('stockLocationIds', {
								initialValue: formData.stockLocationIds,
							})(
								<OWAsyncSelect
									mode="multiple"
									stateSlice={stockLocations}
									valueAccessor={(el) => el.id}
									targetCollectionName="scheduleServiceCallStockLocationDropdown"
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) => {
										fetchStockLocations(
											{ name: searchText },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										);
									}}
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleStockLocations(ids, targetCollectionName);
									}}
									renderRecord={(sl) => (
										<Select.Option key={sl.id} value={sl.id}>
											{nullSafeGet('name', sl)}
										</Select.Option>
									)}
									sortBy={{
										sort_by: 'name',
										order: 'ascend',
									}}
								/>
							)}
						</Form.Item>
					)}
				</Form>
			</Modal>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	isReschedule: ownProps.isReschedule,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,
	currentUser: state.session.currentUser,
	stockLocations: state.stock_locations,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Form.create<ScheduleServiceCallFormProps>()(ScheduleServiceCallModalForm));
